import * as Sentry from '@sentry/nextjs';
import {
  type Basket,
  type CheckoutBasket,
  type OrderDetails,
  type Location,
  type OrgTokenConfig,
} from '@koala/sdk';
import { paymentReducerDigitalWallet } from './payment';
import { ENV } from '@/constants/envConfig';
import { ASAP } from '@/constants/global';
import { type IPaymentState } from '@/types/paymentInfo';

/**
 * Interface for organization context that will be added to all Sentry logs
 */
interface SentryOrganizationContext {
  organizationId: number;
  organizationLabel: string;
  organizationSlug?: string;
}

interface SentryLocationContext {
  locationId: number;
  location_locationId?: string;
  locationLabel: string;
}

/**
 * Singleton Sentry Logger with organization context management
 * This class provides a centralized way to log to Sentry with automatic
 * organization context enrichment. Initialize once in _app.tsx and use
 * throughout the application.
 */
class SentryLogger {
  private static instance: SentryLogger | null = null;
  private organizationContext: SentryOrganizationContext | null = null;
  private locationContext: SentryLocationContext | null = null;

  /**
   * Get the singleton instance of SentryLogger
   */
  public static getInstance(): SentryLogger {
    if (!SentryLogger.instance) {
      SentryLogger.instance = new SentryLogger();
      if (ENV === 'dev') {
        console.log('[SentryLogger] Instance created.');
      }
    }

    return SentryLogger.instance;
  }

  public getOrganizationId() {
    return this.organizationContext?.organizationId;
  }

  /**
   * Set the organization context that will be included in all logs
   * This should be called once from _app.tsx when organization data is loaded
   */
  public setOrganizationContext(organization: OrgTokenConfig): void {
    this.organizationContext = {
      organizationId: organization.id,
      organizationLabel: organization.label,
      organizationSlug: organization?.slug,
    };
  }

  public getLocationId() {
    return this.locationContext?.locationId;
  }

  /**
   * Set the location context that will be included in all logs
   */
  public setLocationContext(selectedLocation: Location): void {
    this.locationContext = {
      locationId: selectedLocation.id,
      location_locationId: selectedLocation.location_id,
      locationLabel: selectedLocation.label,
    };
  }

  private addLogGlobalContext(contextData: Record<string, unknown>): Record<string, unknown> {
    return {
      ...contextData,
      ...this.organizationContext,
      ...this.locationContext,
    };
  }

  /**
   * Log an info message with organization context
   */
  public info(title: string, contextData: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    Sentry.logger.info(title, this.addLogGlobalContext(contextData));
  }

  /**
   * Log a warning message with organization context
   */
  public warn(title: string, contextData: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    Sentry.logger.warn(title, this.addLogGlobalContext(contextData));
  }

  /**
   * Log an error message with organization context
   */
  public error(title: string, contextData: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    Sentry.logger.error(title, this.addLogGlobalContext(contextData));
  }
}

// Export singleton instance
export const sentryLogger = SentryLogger.getInstance();

export const getBasketLogData = (checkoutBasket?: CheckoutBasket | Basket) => {
  return checkoutBasket?.id
    ? {
        basketId: checkoutBasket.id,
        subtotal: checkoutBasket.subtotal,
        tip: checkoutBasket.tip,
        salesTax: checkoutBasket.sales_tax,
        basketTotal: checkoutBasket.total,
        conveyanceType: checkoutBasket.conveyance_type.type,
        conveyance: checkoutBasket.conveyance_type,
        earliestReadyAt: checkoutBasket.earliest_ready_time_at_location,
        basketFees: checkoutBasket.fees,
        loyaltyDiscount: checkoutBasket.loyalty_discount_amount,
        promoDiscount: checkoutBasket.promo_discount_amount,
        giftCardAmountAuthorized: checkoutBasket.gift_card_amount_authorized,
        basketWantedAt: checkoutBasket.wanted_at ? checkoutBasket.wanted_at : ASAP,
      }
    : {};
};

export const getPaymentLogData = (payment: IPaymentState) => {
  return payment
    ? {
        paymentType: payment.paymentType?.type,
        selectedDigitalWallet: payment.selectedDigitalWallet,
      }
    : {};
};

export const getCompletedOrderLogData = (orderDetails: OrderDetails) => {
  const payment = orderDetails?.order_data.payment[0];
  return orderDetails
    ? {
        completedOrderId: orderDetails.id,
        completedOrderPaymentType: payment?.type,
        completedOrderDigitalWallet: paymentReducerDigitalWallet(payment?.digital_wallet_id),
        completedOrderReadyAt: orderDetails.ready_at,
        completedOrderTotal: orderDetails.order_data.total,
      }
    : {};
};
